import { Button, Grid } from "@mui/material";
import { Plus, SquarePen } from "lucide-react";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FormProvider } from "src/components/hook-form";
import useLocales from "src/hooks/useLocales";
import GenerateDocSeriesDialog from "src/pages/ManualDocumentV2/components/GenerateDocSeriesDialog";
import { DocumentSeriesDialog } from "src/pages/ManualDocumentV2/components/ManualDocumentSeries/DocumentSeriesDialog";
import { PATH_DASHBOARD } from "src/routes/paths";
import { SaveAsDraftDialog } from "../components/SaveAsDraftDialog";
import { useFormEngine } from "../core/engine/useFormEngine";
import type { IFormConfig } from "../core/types";
import { DocumentFormSkeleton } from "./DocumentFormSkeleton";
import { DocumentHeader } from "./DocumentHeader";
import { ManualDocumentForm } from "./ManualDocumentForm";

interface IDocumentFormProps {
  config: IFormConfig;
  refreshPage: number;
  onSeriesUpdated: () => void;
}

const flattenErrorMessages = (errors: Record<string, any>): string[] => {
  const out: string[] = [];
  const walk = (node: any) => {
    if (!node) return;
    if (typeof node === "object") {
      if (typeof node.message === "string") out.push(node.message);
      else Object.values(node).forEach(walk);
    }
  };
  walk(errors);
  return out;
};

const DocumentForm = ({
  config,
  refreshPage,
  onSeriesUpdated,
}: IDocumentFormProps) => {
  const { translate } = useLocales("manual_document_screen");
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [isSeriesDialogOpen, setIsSeriesDialogOpen] = useState(false);
  const engine = useFormEngine({ config, refreshPage });

  const {
    methods,
    handleSubmit,
    isSubmitting,
    isSubmittingNew,
    isSubmittingDraft,
    createDocument,
    createNewDocument,
    createDraftDocument,
    taxRates,
    validationError,
    docNumberError,
    isLegalRepValid,
    isLoading,
    isDocSeriesDialogVisible,
    setIsDocSeriesDialogVisible,
    duplicateDocumentNumber,
    handleRefetchDocumentNumber,
    showDialogLeavingPage,
    confirmNavigation,
    cancelNavigation,
    actualCompanyId,
  } = engine;

  if (isLoading) return <DocumentFormSkeleton />;

  const onValidationError = (errors: Record<string, any>) => {
    const messages = flattenErrorMessages(errors);
    enqueueSnackbar(messages[0] ?? translate("please_fix_form_errors"), {
      variant: "error",
    });
  };

  return (
    <FormProvider methods={methods}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <DocumentHeader
            config={config}
            translate={translate}
            onSeriesUpdated={onSeriesUpdated}
          />
        </Grid>
        <Grid item xs={12}>
          <ManualDocumentForm
            translate={translate}
            taxRates={taxRates}
            engine={engine}
            uiBlocks={config.uiBlocks}
            isSubmitting={isSubmitting}
            isSubmittingNew={isSubmittingNew}
            isSubmittingDraft={isSubmittingDraft}
            isLoading={isLoading}
            isLegalRepValid={isLegalRepValid}
            validationError={validationError}
            validationErrorActions={(validationError ?? []).map((_, i) => {
              const isDocNum =
                i === (validationError ?? []).length - 1 && !!docNumberError;
              return (
                <Button
                  key={isDocNum ? "doc-series" : "legal-rep"}
                  variant="outlined"
                  size="medium"
                  endIcon={
                    isDocNum ? (
                      <SquarePen strokeWidth="1.5px" size="16px" />
                    ) : (
                      <Plus strokeWidth="1.5px" size="16px" />
                    )
                  }
                  sx={{
                    fontWeight: "semi-bold",
                    color: (theme) => theme.palette.error.dark,
                    borderColor: (theme) => theme.palette.error.dark,
                    "&:hover": {
                      borderColor: (theme) => theme.palette.error.dark,
                      color: (theme) => theme.palette.error.dark,
                      backgroundColor: "transparent",
                    },
                  }}
                  onClick={() =>
                    isDocNum
                      ? setIsSeriesDialogOpen(true)
                      : navigate(
                          `/dashboard/companies?editCompanyId=${actualCompanyId}&tab=legal_representative`,
                        )
                  }
                >
                  {isDocNum
                    ? translate("configure_document_series")
                    : translate("add_legal_information")}
                </Button>
              );
            })}
            onSubmit={handleSubmit(createDocument, onValidationError)}
            onSubmitAndCreateNew={handleSubmit(
              createNewDocument,
              onValidationError,
            )}
            methods={methods}
            onSaveAsDraft={createDraftDocument}
          />
        </Grid>
      </Grid>

      <GenerateDocSeriesDialog
        isShowModal={isDocSeriesDialogVisible}
        documentNumber={duplicateDocumentNumber}
        showModalMethod={() => setIsDocSeriesDialogVisible(false)}
        onRegenerateClick={handleRefetchDocumentNumber}
      />
      {config.complianceType !== undefined && (
        <DocumentSeriesDialog
          isOpen={isSeriesDialogOpen}
          onClose={() => setIsSeriesDialogOpen(false)}
          onRefreshParent={onSeriesUpdated}
          documentClass={config.documentClass as any}
          complianceType={config.complianceType as any}
        />
      )}

      <SaveAsDraftDialog
        translate={translate}
        showModalMethod={cancelNavigation}
        confirmNavigation={confirmNavigation}
        isShowModal={showDialogLeavingPage}
        createDraftDocument={() => {
          createDraftDocument(methods.getValues());
        }}
        isSubmittingDraft={isSubmittingDraft}
      />
    </FormProvider>
  );
};

export default DocumentForm;
